import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Navbar1 } from "components/navbars";
import { Footer1 } from "components/footer";
import { getBlogsBySlug } from "../../helpers/api/BlogService";
import { toast } from 'react-toastify';
import { Helmet } from "react-helmet-async";
import config from "config";

type FaqItem = {
  question: string;
  answer: string;
};

type BlogDetail = {
  blogTitle: string;
  slug: string;
  shortDescription: string;
  longDescription: string;
  metaKeywords?: string;
  metaDescription?: string;
  blogImage?: string;
  createdOn?: string;
  faqs?: FaqItem[];
};

type Props = {
  blogData?: BlogDetail;
};


export default function IndustryDetailTemplate({ blogData }: Props) {
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const [blog, setBlog] = useState<BlogDetail | null>(blogData || null);
  const [loading, setLoading] = useState(!blogData);

  // useEffect(() => {

  //   const state = location.state as any;
  //   if (state && state.industry) {
  //     setBlog(state.industry);
  //     setLoading(false);
  //   } else if (industryKey) {

  //     getBlogsBySlug(industryKey)
  //       .then((data) => {
  //         if (data && data.success && data.result) {
  //           setBlog(data.result);
  //         } else {
  //           toast.info("No details found for this industry.");
  //         }
  //       })
  //       .catch(() => {
  //         toast.error("Failed to fetch industry details.");
  //       })
  //       .finally(() => setLoading(false));
  //   } else {
  //     setLoading(false);
  //   }
  // }, [industryKey, location.state]);

  useEffect(() => {
    if (blogData) {
      setBlog(blogData);
      setLoading(false);
      return;
    }

    const state = location.state as any;

    if (state && state.industry) {
      setBlog(state.industry);
      setLoading(false);
    } else if (slug) {
      getBlogsBySlug(slug)
        .then((data) => {
          if (data && data.success && data.result) {
            setBlog(data.result);
          } else {
            toast.info("No details found for this industry.");
          }
        })
        .catch(() => {
          toast.error("Failed to fetch industry details.");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [slug, location.state, blogData]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Industry CRM Solution | SalesTrackify</title>
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={`${config.DOMAIN_URL}/${slug}`} />
        </Helmet>
        <div className="container py-5 text-center">Loading...</div>
      </>
    );
  }

  if (!blog) {
    return (
      <>
        <Helmet>
          <title>Industry Not Found | SalesTrackify</title>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href={`${config.DOMAIN_URL}/${slug}`} />
        </Helmet>
        <div className="container py-5 text-center">No details found for this industry.</div>
      </>
    );
  }

  const canonicalUrl = `${config.DOMAIN_URL}/${blog.slug || "industry"}`;
  const description = blog.metaDescription || blog.shortDescription;
  const image = blog.blogImage || `${config.DOMAIN_URL}/seo/trackify-preview.png`;

  return (
    <>

      <Helmet>
        <title>{`${blog.blogTitle} | Sales Trackify CRM & Sales Management`}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={blog.metaKeywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={blog.blogTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={image} />
        <meta property="og:site_name" content="SalesTrackify" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.blogTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />

        {/* JSON-LD Article Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': blog.blogTitle,
            'description': description,
            'image': image,
            'author': {
              '@type': 'Organization',
              'name': 'SalesTrackify'
            },
            'publisher': {
              '@type': 'Organization',
              'name': 'SalesTrackify',
              'logo': {
                '@type': 'ImageObject',
                'url': `${config.DOMAIN_URL}/logo.png`
              }
            },
            'datePublished': blog.createdOn,
            'url': canonicalUrl
          })
        }} />
        {blog.faqs && blog.faqs.length > 0 && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              'mainEntity': blog.faqs.map(faq => ({
                '@type': 'Question',
                'name': faq.question,
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': faq.answer
                }
              }))
            })
          }} />
        )}
      </Helmet>
      <Navbar1 hideSearch fixedWidth navClass="navbar-light zindex-10" buttonClass="btn-outline-secondary btn-sm" />
      <div >
        {/* Hero Section */}
        <section
          style={{
            background: 'linear-gradient(90deg, #1e3a8a22, #2dd4bf22, #1e3a8a22)',
            padding: '70px 0 50px 0',
            textAlign: 'center',
            borderBottomLeftRadius: '30px',
            borderBottomRightRadius: '30px',
            marginLeft: 25,
            marginRight: 25,
            marginBottom: '2.5rem',
          }}
        >
          <h1 style={{ fontSize: '40px', fontWeight: 700, color: '#24347D' }}>{blog.blogTitle}</h1>
          <p style={{ marginTop: '10px', fontSize: '18px', color: '#6c757d', textAlign: 'center', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }} className="px-5">
            {blog.shortDescription}
          </p>
          {/* <Link to="/signup" className="btn btn-primary mx-2">Try Free</Link> */}
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition-all hover:scale-105 shadow-lg"
            style={{
              backgroundColor: "#26A2AB",
              color: "white"
            }}
          >
            Book a Demo
          </Link>
        </section>
        <div className="container py-4">
          {/* Long Description (HTML) */}
          {blog.longDescription && (
            <section className="mb-5">
              <div dangerouslySetInnerHTML={{ __html: blog.longDescription }} />
            </section>
          )}

          {/* FAQ Section (Schema-Ready) */}
          {blog.faqs && blog.faqs.length > 0 && (
            <section className="mb-5">
              <h2 className="mb-4 text-center">Frequently Asked Questions</h2>
              <div className="accordion" id="faqAccordion">
                {blog.faqs.map((faq, idx) => (
                  <div className="accordion-item" key={idx} style={{ border: 'none', marginBottom: 12, borderRadius: 12, boxShadow: '0 2px 12px 0 rgba(36,52,125,0.07)' }}>
                    <h3 className="accordion-header" id={`faq-heading-${idx}`} style={{ margin: 0 }}>
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#faq-collapse-${idx}`}
                        aria-expanded="false"
                        aria-controls={`faq-collapse-${idx}`}
                        style={{ fontWeight: 600, fontSize: 18, background: '#f8fafc', borderRadius: 12 }}
                      >
                        {faq.question}
                      </button>
                    </h3>
                    <div
                      id={`faq-collapse-${idx}`}
                      className="accordion-collapse collapse"
                      aria-labelledby={`faq-heading-${idx}`}
                      data-bs-parent="#faqAccordion"
                    >
                      <div className="accordion-body" style={{ fontSize: 16, color: '#444', background: '#fff', borderRadius: 12 }}>
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="text-center mt-5">
            {/* <Link to="/signup" className="btn btn-primary mx-2">Try Free</Link> */}
            <Link to="/contact" className="btn btn-outline-primary mx-2">Request Demo</Link>
          </section>
        </div>
      </div>
      <Footer1 />
    </>
  );
}
